import React from "react";
import { saveAs } from "file-saver";
import { pdf } from "@react-pdf/renderer";
import MyInvoiceDoc from "containers/pdf/billingPdf";
import { getSelectedClientDetails } from "services/clientdetails";
import { toast } from "react-toastify";
import { sendDietPdf, generateDietChartPDF } from "firebaseConfig";
import { storeInvoicePdf } from "services/billing";
import { createInvoiceId, generateFileName } from "./common"; 
import { storeProductInvoicePdf } from "services/billing";
import { storeConsultationInvoicePdf } from "services/billing";
import { deduplicateDietCharts } from "containers/pdf/utils";
import {
  expandPerDayChartToPdfSlices,
  buildClientPortalChartData,
} from "utils/perDayDietChart";

let isGeneratingPDF = false;

export const generatePDFDocument = async (
  dietChartSettings,
  chartArray,
  selectedClient,
  profile,
  setLoader,
  mail,
  dietMealTimings,
  previewOnly = false,
  adminDetails,
  options = {}
) => {
  const { clientPortalPdf = false } = options;
  console.log("=== PDF GENERATION START ===");
  console.log("Client:", selectedClient?.name, selectedClient?.email);
  console.log("Chart Array Length:", chartArray?.length);
  // console.log("Chart Array:", JSON.stringify(chartArray, null, 2));

  if (isGeneratingPDF) {
    console.warn("PDF generation already in progress");
    toast.warning("PDF generation already in progress. Please wait...");
    return;
  }

  let chartData = [];

  try {
    console.log("Step 1: Validating inputs");
    if (!selectedClient || !selectedClient.id) {
      throw new Error("Client information is missing. Please select a client.");
    }

    if (!chartArray || chartArray.length === 0) {
      throw new Error(
        "No diet charts selected. Please select at least one diet chart."
      );
    }

    if (!dietChartSettings) {
      throw new Error("Diet chart settings are missing. Please try again.");
    }

    isGeneratingPDF = true;
    setLoader(true);
    console.log("Step 2: Loader set to true");

    console.log("Step 3: Fetching client details");
    const CLIENT_DETAILS_TIMEOUT = 10000;
    let selectedClient_ = await Promise.race([
      getSelectedClientDetails({
        id: selectedClient.id,
      }),
      new Promise((_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error("Failed to fetch client details. Please try again.")
            ),
          CLIENT_DETAILS_TIMEOUT
        )
      ),
    ]);
    console.log("Step 4: Client details fetched");
    selectedClient_ = { ...selectedClient_, ...selectedClient };

    console.log("Step 4.1: Validating client measurement data");
    if (selectedClient_?.measurement) {
      if (!Array.isArray(selectedClient_.measurement)) {
        console.warn(
          "Warning: measurement is not an array, converting to empty array"
        );
        selectedClient_.measurement = [];
      } else {
        console.log(
          "Measurement array length:",
          selectedClient_.measurement.length
        );
        const invalidMeasurements = selectedClient_.measurement.filter(
          (m, idx) => {
            if (!m || typeof m !== "object") {
              console.warn(`Invalid measurement at index ${idx}:`, m);
              return true;
            }
            if (
              m.timestamp &&
              !m.timestamp.seconds &&
              typeof m.timestamp !== "string"
            ) {
              console.warn(
                `Invalid timestamp at measurement index ${idx}:`,
                m.timestamp
              );
              return true;
            }
            return false;
          }
        );
        if (invalidMeasurements.length > 0) {
          console.warn(
            `Filtering out ${invalidMeasurements.length} invalid measurements`
          );
          selectedClient_.measurement = selectedClient_.measurement.filter(
            (m, idx) => !invalidMeasurements.includes(m)
          );
        }
      }
    } else {
      console.log("No measurement data found, setting to empty array");
      selectedClient_.measurement = [];
    }

    if (selectedClient_?.vital) {
      if (!Array.isArray(selectedClient_.vital)) {
        console.warn(
          "Warning: vital is not an array, converting to empty array"
        );
        selectedClient_.vital = [];
      }
    } else {
      selectedClient_.vital = [];
    }

    console.log("Step 4.2: Client data validated");

    console.log("Step 5: Processing chart data");
    console.log("Chart array items:", chartArray.length);

    if (clientPortalPdf) {
      chartData = [];
      for (let i = 0; i < chartArray.length; i++) {
        const dietChart = chartArray[i]?.dietChart;
        if (!dietChart) continue;
        chartData.push(...buildClientPortalChartData(dietChart));
      }
      if (chartData.length === 0) {
        throw new Error(
          "No meal data found for the selected diet chart(s)."
        );
      }
    } else for (let i = 0; i < chartArray.length; i++) {
      console.log(`Processing chart ${i + 1}/${chartArray.length}`);

      if (!chartArray[i] || !chartArray[i].dietChart) {
        console.warn(`Skipping invalid chart at index ${i}:`, chartArray[i]);
        continue;
      }

      let dietChart = chartArray[i].dietChart;
      console.log(`Chart ${i} - DietChartId:`, dietChart.dietChartId);
      console.log(`Chart ${i} - Keys:`, Object.keys(dietChart));

      const perDaySlices = expandPerDayChartToPdfSlices(dietChart);
      if (perDaySlices && perDaySlices.length > 0) {
        for (let s = 0; s < perDaySlices.length; s++) {
          const slice = perDaySlices[s];
          chartData.push({
            dietChartId: slice.dietChartId,
            date: slice.date,
            weekPlan: slice.weekPlan,
            chartType: slice.chartType,
            notes: slice.notes || "",
            perDayTrailingNotes: slice.perDayTrailingNotes || "",
            from: slice.from,
            to: slice.to,
            layoutVariant: slice.layoutVariant,
            table: slice.table,
          });
        }
        continue;
      }
      if (perDaySlices && perDaySlices.length === 0) {
        continue;
      }

      let data = {
        dietChartId: dietChart.dietChartId,
        date: dietChart.date,
        weekPlan: dietChart.weekPlan,
        chartType: dietChart.chartType,
        notes: dietChart.notes || "",
        from: dietChart.from,
        to: dietChart.to,
        table: [],
      };

      let keys = Object.keys(dietChart);
      console.log(`Chart ${i} - Processing ${keys.length} keys`);

      for (let k of keys) {
        if (
          ![
            "date",
            "from",
            "to",
            "notes",
            "clientId",
            "dietChartId",
            "weekPlan",
            "chartType",
            "createdAt",
            "selected",
            "layoutVariant",
            "dayPlans",
            "dayScheduleKeys",
            "perDayTrailingNotes",
          ].includes(k)
        ) {
          if (Array.isArray(dietChart[k])) {
            console.log(
              `Chart ${i} - Adding timing ${k} with ${dietChart[k].length} items`
            );

            if (dietChart[k].length > 100) {
              console.warn(
                `Chart ${i} - Timing ${k} has ${dietChart[k].length} items (very large!)`
              );
            }

            try {
              const sanitizedList = dietChart[k]
                .map((item, idx) => {
                  if (!item || typeof item !== "object") {
                    console.warn(
                      `Chart ${i} - Timing ${k} - Item ${idx} is invalid:`,
                      item
                    );
                    return null;
                  }

                  if (
                    item.image &&
                    typeof item.image === "string" &&
                    item.image.length > 10000
                  ) {
                    console.warn(
                      `Chart ${i} - Timing ${k} - Item ${idx} has very large image URL`
                    );
                  }

                  return item;
                })
                .filter((item) => item !== null);

              if (sanitizedList.length !== dietChart[k].length) {
                console.warn(
                  `Chart ${i} - Timing ${k} - Filtered out ${
                    dietChart[k].length - sanitizedList.length
                  } invalid items`
                );
              }

              data.table.push({ timing: k, list: sanitizedList });
            } catch (sanitizeError) {
              console.error(
                `Chart ${i} - Timing ${k} - Error sanitizing:`,
                sanitizeError
              );
              console.error(
                `Chart ${i} - Timing ${k} - Data:`,
                JSON.stringify(dietChart[k].slice(0, 2), null, 2)
              );
              throw new Error(
                `Error processing timing ${k} in chart ${i}: ${sanitizeError.message}`
              );
            }
          } else {
            console.warn(
              `Chart ${i} - Timing ${k} is not an array:`,
              typeof dietChart[k],
              dietChart[k]
            );
          }
        }
      }
      chartData.push(data);
      console.log(`Chart ${i} - Processed successfully`);
    }

    console.log("Step 6: Validating processed data");
    if (chartData.length === 0) {
      throw new Error(
        "No valid diet chart data found. Please check your selections."
      );
    }

    console.log("Step 7: Sorting and deduplicating");
    try {
      chartData = chartData.sort((a, b) => {
        try {
          const dateA = a.date ? new Date(a.date) : new Date(0);
          const dateB = b.date ? new Date(b.date) : new Date(0);
          return dateA - dateB;
        } catch (sortError) {
          console.error("Error sorting dates:", sortError, {
            a: a.date,
            b: b.date,
          });
          return 0;
        }
      });
      console.log("After sort:", chartData.length, "charts");

      console.log("Step 7.1: Using backend PDF generation");
    } catch (sortError) {
      console.error("Error during sort:", sortError);
      throw new Error(`Error sorting diet charts: ${sortError.message}`);
    }

    try {
      chartData = deduplicateDietCharts(chartData);
      console.log("After deduplication:", chartData.length, "charts");
    } catch (dedupError) {
      console.error("Error during deduplication:", dedupError);
      throw new Error(`Error deduplicating diet charts: ${dedupError.message}`);
    }

    const totalTableItems = chartData.reduce(
      (sum, chart) => sum + chart.table.length,
      0
    );
    const totalRecipeItems = chartData.reduce((sum, chart) => {
      return (
        sum +
        chart.table.reduce((tableSum, tableItem) => {
          return tableSum + (tableItem.list ? tableItem.list.length : 0);
        }, 0)
      );
    }, 0);

    console.log("Total table items across all charts:", totalTableItems);
    console.log("Total recipe items across all charts:", totalRecipeItems);

    if (totalRecipeItems > 1000) {
      console.warn(
        `WARNING: Very large dataset - ${totalRecipeItems} total recipe items. PDF generation may take a long time.`
      );
    }

    const chartDataSize = JSON.stringify(chartData).length;
    console.log("Chart data JSON size:", chartDataSize, "bytes");
    if (chartDataSize > 5000000) {
      console.warn(
        `WARNING: Very large chart data - ${chartDataSize} bytes. This may cause performance issues.`
      );
    }

    if (chartData.length === 0) {
      throw new Error(
        "No valid diet chart data found. Please check your selections."
      );
    }

    console.log(
      "Step 8: Sanitizing data for backend (removing NaN, Infinity, and invalid values)"
    );

    // Helper function to recursively sanitize NaN, Infinity, and other non-serializable values
    const sanitizeForJSON = (obj, depth = 0) => {
      // Prevent infinite recursion
      if (depth > 50) {
        console.warn("Sanitization depth limit reached, skipping deep object");
        return null;
      }

      if (obj === null || obj === undefined) {
        return null;
      }

      // Handle numbers - replace NaN, Infinity, -Infinity with null
      if (typeof obj === "number") {
        if (isNaN(obj) || !isFinite(obj)) {
          console.warn(
            "Found invalid number value:",
            obj,
            "replacing with null"
          );
          return null;
        }
        return obj;
      }

      // Handle Date objects - convert to ISO string
      if (obj instanceof Date) {
        return obj.toISOString();
      }

      // Handle Moment objects (if moment.js is used)
      if (obj && typeof obj === "object" && obj._isAMomentObject) {
        return obj.toISOString();
      }

      // Handle Firestore Timestamp objects
      if (
        obj &&
        typeof obj === "object" &&
        obj.seconds !== undefined &&
        obj.nanoseconds !== undefined
      ) {
        return {
          seconds: obj.seconds,
          nanoseconds: obj.nanoseconds,
          _type: "timestamp",
        };
      }

      // Handle arrays
      if (Array.isArray(obj)) {
        return obj.map((item) => sanitizeForJSON(item, depth + 1));
      }

      // Handle objects
      if (typeof obj === "object" && obj.constructor === Object) {
        const sanitized = {};
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            try {
              sanitized[key] = sanitizeForJSON(obj[key], depth + 1);
            } catch (e) {
              console.warn(`Error sanitizing key "${key}":`, e);
              sanitized[key] = null;
            }
          }
        }
        return sanitized;
      }

      // Handle functions - skip them
      if (typeof obj === "function") {
        return undefined;
      }

      // Handle other primitives (string, boolean, etc.)
      return obj;
    };

    const sanitizedChartData = sanitizeForJSON(chartData);
    const sanitizedSelectedClient = sanitizeForJSON(selectedClient_);
    const sanitizedDietChartSettings = sanitizeForJSON(dietChartSettings);
    const sanitizedDietMealTimings = sanitizeForJSON(dietMealTimings);
    const sanitizedAdminDetails = sanitizeForJSON(adminDetails);
    const sanitizedProfile = sanitizeForJSON(profile);

    // Double-check: Try to stringify to catch any remaining issues
    try {
      JSON.stringify(sanitizedChartData);
      console.log("Step 8.0.5: Chart data is JSON-serializable");
    } catch (stringifyError) {
      console.error(
        "Step 8.0.5 ERROR: Chart data still contains non-serializable values:",
        stringifyError
      );
      throw new Error(`Data sanitization failed: ${stringifyError.message}`);
    }

    console.log(
      "Step 8.1: Data sanitized, calling backend PDF generation service"
    );

    const result = await generateDietChartPDF({
      chartData: sanitizedChartData,
      selectedClient: sanitizedSelectedClient,
      dietChartSettings: sanitizedDietChartSettings,
      dietMealTimings: sanitizedDietMealTimings,
      adminDetails: sanitizedAdminDetails,
      profile: sanitizedProfile,
    });

    if (!result.data || !result.data.success) {
      throw new Error(result.data?.error || "Backend PDF generation failed");
    }

    console.log(result.data, "Step 9: Backend PDF generated successfully");

    const pdfBase64 = result.data.pdfData;
    const fileName = result.data.fileName || `${selectedClient.name}.pdf`;

    const binaryString = atob(pdfBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: "application/pdf" });

    if (previewOnly) {
      const blobUrl = URL.createObjectURL(blob);
      setLoader(false);
      return blobUrl;
    }

    if (mail && selectedClient.email) {
      console.log("Step 10: Sending PDF via email");
      try {
        await sendPDF(
          selectedClient.name,
          selectedClient.email,
          blob,
          adminDetails.name || "N/A"
        );
        console.log("Step 11: Email sent successfully");
        toast.success("Diet chart sent successfully!");
        setLoader(false);
      } catch (error) {
        console.error("Step 11 ERROR: Mail send failed:", error);
        toast.error("Failed to send diet chart. Please try again.");
        setLoader(false);
      }
    } else {
      console.log("Step 10: Downloading PDF");
      saveAs(blob, fileName);
      console.log("Step 11: PDF download initiated");
      setLoader(false);
    }

    console.log("=== PDF GENERATION SUCCESS (Backend) ===");
  } catch (err) {
    console.error("=== PDF GENERATION FAILED ===");
    console.error("Error:", err);
    console.error("Error message:", err.message);
    console.error("Error stack:", err.stack);
    console.error("Chart data at error:", chartData);
    console.error("Selected client:", selectedClient);
    const errorMessage =
      err.message ||
      "Something went wrong while generating PDF. Please try again.";
    toast.error(errorMessage);
    setLoader(false);
  } finally {
    isGeneratingPDF = false;
    console.log("=== PDF GENERATION COMPLETE (finally block) ===");
  }
};

export const invoiceBillingPdf = async ({
  user,
  bill,
  termsAndConditions,
  type,
  shouldDownload = true,
}) => {
  try {
    let fileName;
    const invoiceId = bill.invoiceId || createInvoiceId();
    const billWithInvoiceId = {
      ...bill,
      invoiceId,
      sellerGstNumber: bill.sellerGstNumber || user?.gstNumber,
    };
    const blob = await pdf(
      <MyInvoiceDoc
        user={user}
        bill={billWithInvoiceId}
        note={termsAndConditions}
        type={type}
      />
    ).toBlob();
    if (billWithInvoiceId.type === "consultation") {
      fileName = generateFileName(billWithInvoiceId.serviceName, invoiceId);
      await storeConsultationInvoicePdf(
        billWithInvoiceId.clientId,
        billWithInvoiceId.id,
        blob,
        fileName,
        termsAndConditions,
        billWithInvoiceId,
        invoiceId
      );
    } else if (!billWithInvoiceId.serviceName) {
      fileName = generateFileName(billWithInvoiceId.productName, invoiceId);
      await storeProductInvoicePdf(
        billWithInvoiceId.clientId,
        billWithInvoiceId.id,
        blob,
        fileName,
        termsAndConditions,
        billWithInvoiceId,
        invoiceId
      );
    } else {
      fileName = generateFileName(billWithInvoiceId.serviceName, invoiceId);
      await storeInvoicePdf(
        billWithInvoiceId.clientId,
        billWithInvoiceId.id,
        blob,
        fileName,
        termsAndConditions,
        billWithInvoiceId,
        invoiceId
      );
    }
    if (shouldDownload) {
      saveAs(blob, fileName);
    }
  } catch (error) {
    throw error;
  }
};

function sendPDF(name, email, pdfBlob, adminName) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = async () => {
      try {
        const base64data = reader.result.split(",")[1];

        await sendDietPdf({
          clientName: name,
          clientEmail: email,
          pdfData: base64data,
          adminName,
        });
        resolve();
      } catch (error) {
        console.error("Error sending diet PDF:", error);
        reject(error);
      }
    };

    reader.onerror = () => {
      const error = new Error("Failed to read PDF file");
      reject(error);
    };

    reader.readAsDataURL(pdfBlob);
  });
}

export const getMinTimestampObject = (item) => {
  if (!item || !Array.isArray(item) || item.length === 0) {
    console.warn(
      "getMinTimestampObject: Invalid or empty item array provided."
    );
    return null;
  }

  const data = item.filter((entry) => {
    if (
      !entry ||
      typeof entry !== "object" ||
      entry?.weight === "" ||
      entry?.weight === null ||
      entry?.weight === undefined
    ) {
      console.warn(
        "getMinTimestampObject: Skipping invalid entry (missing/empty weight):",
        entry
      );
      return false;
    }
    if (!entry.timestamp) {
      console.warn(
        "getMinTimestampObject: Skipping entry with missing timestamp:",
        entry
      );
      return false;
    }
    return true;
  });

  if (data.length === 0) {
    console.warn(
      "getMinTimestampObject: No valid data entries with weight and timestamp found after filtering."
    );
    return null;
  }

  let minTimestampObject = data[0];

  for (let i = 1; i < data.length; i++) {
    let currentTimestamp;
    let minTimestamp;

    try {
      currentTimestamp = parseTimestamp(data[i].timestamp);
      minTimestamp = parseTimestamp(minTimestampObject.timestamp);

      if (currentTimestamp === null || minTimestamp === null) {
        console.warn(
          "getMinTimestampObject: Skipping comparison due to invalid timestamp parsing."
        );
        continue;
      }

      if (currentTimestamp < minTimestamp) {
        minTimestampObject = data[i];
      }
    } catch (e) {
      console.error(
        "getMinTimestampObject: Error comparing timestamps:",
        e,
        "Data entry:",
        data[i]
      );
    }
  }
  return minTimestampObject;
};

const parseTimestamp = (timestamp) => {
  if (timestamp === null || timestamp === undefined) {
    return null;
  }
  if (timestamp.seconds) {
    return new Date(
      timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000
    ).getTime();
  } else if (typeof timestamp === "string" || typeof timestamp === "number") {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date.getTime();
  }
  console.warn("parseTimestamp: Invalid timestamp format:", timestamp);
  return null;
};

export const getMaxTimestampObject = (item) => {
  if (!item || !Array.isArray(item) || item.length === 0) {
    console.warn(
      "getMaxTimestampObject: Invalid or empty item array provided."
    );
    return null;
  }

  const data = item.filter((entry) => {
    if (
      !entry ||
      typeof entry !== "object" ||
      entry?.weight === "" ||
      entry?.weight === null ||
      entry?.weight === undefined
    ) {
      console.warn(
        "getMaxTimestampObject: Skipping invalid entry (missing/empty weight):",
        entry
      );
      return false;
    }
    if (!entry.timestamp) {
      console.warn(
        "getMaxTimestampObject: Skipping entry with missing timestamp:",
        entry
      );
      return false;
    }
    return true;
  });

  if (data.length === 0) {
    console.warn(
      "getMaxTimestampObject: No valid data entries with weight and timestamp found after filtering."
    );
    return null;
  }

  let maxTimestampObject = data[0];

  for (let i = 1; i < data.length; i++) {
    let currentTimestamp;
    let maxTimestamp;

    try {
      currentTimestamp = parseTimestamp(data[i].timestamp);
      maxTimestamp = parseTimestamp(maxTimestampObject.timestamp);

      if (currentTimestamp === null || maxTimestamp === null) {
        console.warn(
          "getMaxTimestampObject: Skipping comparison due to invalid timestamp parsing."
        );
        continue;
      }

      if (currentTimestamp > maxTimestamp) {
        maxTimestampObject = data[i];
      }
    } catch (e) {
      console.error(
        "getMaxTimestampObject: Error comparing timestamps:",
        e,
        "Data entry:",
        data[i]
      );
    }
  }
  return maxTimestampObject;
};

export const extractPDFfilename = (url) => {
  // Split the URL by '/' to get segments
  const segments = url.split("/");
  // Get the last segment
  const lastSegment = segments[segments.length - 1];
  // Decode URI component to handle special characters
  const decodedSegment = decodeURIComponent(lastSegment);
  // Remove the directory path and return the filename
  const querySplit = decodedSegment.split("?")[0];

  const fileName = querySplit?.split("/")[1];

  return fileName;
};
